import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageTemplate } from '@/components/templates'
import { FundingRoundsTable } from './components'
import { useFundingRoundsPagination } from '@/hooks/useFundingRoundsPagination'
import { useProjectsStats } from '@/hooks/useOTC'
import { formatCompactNumber } from '@/utils'
import { CONTAINER_SIZES } from '@/constants'
import { FundingMetricCard, MiniBarChart, MiniLineChart, MiniAreaChart, MiniWaveChart } from '@/components/molecules'
import {
  buildFundingBarSeries,
  buildInvestorsLineSeries,
  buildProjectsAreaSeries,
  buildRoundsGaugeProgress,
} from '@/components/molecules/charts/fundingSeries'

const FundingRoundsScreenDesktop = () => {
  const navigate = useNavigate()
  const { data: stats, loading: statsLoading } = useProjectsStats()

  const projectsAreaSeries = useMemo(
    () => buildProjectsAreaSeries(stats?.total_projects, stats?.total_projects_added_30d_pct_change),
    [stats?.total_projects, stats?.total_projects_added_30d_pct_change],
  )

  const roundsGaugeProgress = useMemo(
    () =>
      buildRoundsGaugeProgress(
        stats?.total_funding_rounds,
        stats?.total_funding_rounds_added_30d,
        stats?.total_funding_rounds_added_30d_pct_change,
      ),
    [
      stats?.total_funding_rounds,
      stats?.total_funding_rounds_added_30d,
      stats?.total_funding_rounds_added_30d_pct_change,
    ],
  )

  const fundingBarSeries = useMemo(
    () => buildFundingBarSeries(stats?.average_funding_round_size, stats?.total_funding_rounds),
    [stats?.average_funding_round_size, stats?.total_funding_rounds],
  )

  const investorsLineSeries = useMemo(
    () =>
      buildInvestorsLineSeries(
        stats?.total_investors,
        stats?.total_investors_added_30d,
        stats?.total_investors_added_30d_pct_change,
      ),
    [stats?.total_investors, stats?.total_investors_added_30d, stats?.total_investors_added_30d_pct_change],
  )

  return (
    <PageTemplate maxWidth={CONTAINER_SIZES.WIDE}>
      <div className="space-y-6">
        <div className="flex flex-col items-start">
          <h1 className="mb-2 font-plus-jakarta text-[30px] font-semibold leading-[40px] tracking-[-0.28px] text-[#2d2923] dark:text-[#fdfdfd]">
            Funding Rounds
          </h1>
          <p className="w-[385px] font-plus-jakarta text-[16px] font-normal leading-[24px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
            Access institutional-grade project funding data
          </p>
        </div>

        {statsLoading ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-[124px] bg-white dark:bg-[#121212] animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <FundingMetricCard
              icon="projects"
              label="Total Projects"
              value={stats ? formatCompactNumber(stats.total_projects) : '—'}
              percentChange={
                stats && stats.total_projects_added_30d_pct_change
                  ? {
                      value: `${Math.abs(stats.total_projects_added_30d_pct_change).toFixed(1)}%`,
                      direction: (stats.total_projects_added_30d ?? 0) >= 0 ? 'up' : 'down',
                      color: (stats.total_projects_added_30d ?? 0) >= 0 ? 'green' : 'red',
                    }
                  : undefined
              }
              chart={<MiniAreaChart data={projectsAreaSeries} />}
            />
            <FundingMetricCard
              icon="rounds"
              label="Total Funding Rounds"
              value={stats ? formatCompactNumber(stats.total_funding_rounds ?? 0) : '—'}
              percentChange={
                stats && stats.total_funding_rounds_added_30d_pct_change
                  ? {
                      value: `${Math.abs(stats.total_funding_rounds_added_30d_pct_change).toFixed(0)}%`,
                      direction: (stats.total_funding_rounds_added_30d ?? 0) >= 0 ? 'up' : 'down',
                      color: (stats.total_funding_rounds_added_30d ?? 0) >= 0 ? 'green' : 'red',
                    }
                  : undefined
              }
              chart={<MiniWaveChart progress={roundsGaugeProgress} />}
            />
            <FundingMetricCard
              icon="amount"
              label="Average Funding Round Size"
              value={
                stats ? (
                  <>
                    <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>{' '}
                    {formatCompactNumber(stats.average_funding_round_size ?? 0)}
                  </>
                ) : (
                  '—'
                )
              }
              chart={<MiniBarChart data={fundingBarSeries} />}
            />
            <FundingMetricCard
              icon="investors"
              label="Total Investors"
              value={stats ? formatCompactNumber(stats.total_investors ?? 0) : '—'}
              percentChange={
                stats && stats.total_investors_added_30d && stats.total_investors_added_30d_pct_change
                  ? {
                      value: `${stats.total_investors_added_30d} (${Math.abs(stats.total_investors_added_30d_pct_change).toFixed(1)}%)`,
                      direction: stats.total_investors_added_30d >= 0 ? 'up' : 'down',
                      color: stats.total_investors_added_30d >= 0 ? 'green' : 'red',
                    }
                  : undefined
              }
              chart={<MiniLineChart data={investorsLineSeries} />}
            />
          </div>
        )}

        <FundingRoundsTable
          title="Funding Rounds"
          navigate={navigate}
          useDataHook={useFundingRoundsPagination}
          dataKey="projects"
          showPagination={true}
        />
      </div>
    </PageTemplate>
  )
}

export default FundingRoundsScreenDesktop
